import { useState, useEffect, useMemo } from 'react';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { MdEdit as EditIcon, MdDeleteOutline as DeleteIcon, MdOutlineCategory as CategoryIcon } from 'react-icons/md';
import BorderBlueButtonV2 from '@CommonRepresentationalDisplay/Buttons/BorderBlueButtonV2';
import KaiziqResolveConnectionService from '@Helpers/URLHelpers/KaiziqResolveConnectionService';
import ErrorHandler from '@Root/ErrorHandler';
import AddPriorityRuleModal from './AddPriorityRuleModal/AddPriorityRuleModal';
import './PriorityCategoryMatrix.scss';

const cn = 'priorityCategoryMatrix';

const PRIORITY_CONFIG = {
    critical: { label: 'Critical', color: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)' },
    high:     { label: 'High',     color: '#f97316', bg: 'rgba(249, 115, 22, 0.1)' },
    normal:   { label: 'Normal',   color: '#2784b9', bg: 'rgba(39, 132, 185, 0.1)' },
};

export default function PriorityCategoryMatrix(props) {
    const [cursor, setCursor] = useState('auto');
    const [formFieldList, setFormFieldList] = useState([]);
    const [selectedPriority, setSelectedPriority] = useState('critical');
    const [ruleTypes] = useState(['reporter', 'field']);
    const [fieldParameter, setFieldParameter] = useState([]);
    const [conditions] = useState(['EQUALS', 'NOT EQUALS', 'CONTAINS']);
    const [comparisonOperators] = useState(['EQUALS', 'GRATER THEN', 'LESS THEN']);
    const [property] = useState(['fullName', 'email', 'phoneNumber']);
    const [kaiziqResolveConnection, setKaiziqResolveConnection] = useState({});
    const [rules, setRules] = useState({ critical: [], high: [], normal: [] });
    const [snackBarOpen, setSnackBarOpen] = useState(false);
    const [snackBarType, setSnackBarType] = useState('success');
    const [snackBarMessage, setSnackBarMessage] = useState('');
    const [showAddRuleModal, setShowAddRuleModal] = useState(false);
    const [showEditRuleModal, setShowEditRuleModal] = useState(false);
    const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
    const [tempModalRule, setTempModalRule] = useState(null);
    const [currentEditingRule, setCurrentEditingRule] = useState(null);
    const [ruleToDelete, setRuleToDelete] = useState(null);
    const [validationError, setValidationError] = useState('');
    const [isSavingRuleModal, setIsSavingRuleModal] = useState(false);

    const kaiziqResolveConnectionService = new KaiziqResolveConnectionService();
    const errorHandler = new ErrorHandler();

    useEffect(() => {
        renderData();
    }, []);

    const renderData = async () => {
        try {
            setCursor('wait');
            if (!!props.formFieldList) {
                let formIdList = props.formFieldIdList;
                let formFields = [];
                let fieldParameterList = [];
                for (let itemObj of props.formFieldList) {
                    if (formIdList.includes(itemObj.id)) {
                        itemObj = { ...itemObj, options: JSON.parse(itemObj.options) };
                        formFields.push(itemObj);
                        fieldParameterList.push({ id: itemObj.id, type: itemObj.type, title: itemObj.title });
                    }
                }
                setFieldParameter(fieldParameterList);
                setFormFieldList(formFields);
                await renderKaiziqResolveConnection();
            }
        } catch (err) {
            errorHandler.handleError(err);
        }
        setCursor('auto');
    };

    const renderKaiziqResolveConnection = async () => {
        try {
            let connObj = await kaiziqResolveConnectionService.getKaiziqResolveConnectionById(props.kaiziqResolveConnectionId);
            connObj = JSON.parse(connObj.body);
            connObj = { ...connObj, details: JSON.parse(connObj.details) };
            setKaiziqResolveConnection(connObj);
            setRules(connObj.details?.priorityRules ?? { critical: [], high: [], normal: [] });
        } catch (err) {
            errorHandler.handleError(err);
        }
    };

    // ── Validation helpers ────────────────────────────────────────────────────

    const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const validateDomain = (domain) => /^[a-zA-Z0-9][a-zA-Z0-9.-]*\.[a-zA-Z]{2,}$/.test(domain);
    const validatePhoneNumber = (phone) => /^[\d\s\-+()]{7,}$/.test(phone);

    const validateByPropertyAndCondition = (prop, condition, value) => {
        if (!value) return '';
        if (prop === 'email') {
            if (condition === 'EQUALS' || condition === 'NOT EQUALS') {
                if (!validateEmail(value)) return 'Enter a full email address (e.g. user@example.com)';
            } else if (condition === 'DOMAIN') {
                if (!validateDomain(value)) return 'Enter a domain only (e.g. example.com)';
            }
        }
        if (prop === 'phoneNumber' && !validatePhoneNumber(value)) {
            return 'Please enter a valid phone number';
        }
        return '';
    };

    const getAvailableConditions = () => {
        if (tempModalRule?.property === 'email') return [...conditions, 'DOMAIN'];
        return conditions;
    };

    // ── Modal handlers ────────────────────────────────────────────────────────

    const handleOpenAddRuleModal = () => {
        setTempModalRule({
            id: Date.now(),
            type: 'reporter',
            property: '',
            priority: selectedPriority,
            fieldId: 0,
            condition: '',
            paramValue: '',
        });
        setValidationError('');
        setShowAddRuleModal(true);
    };

    const handleCloseAddRuleModal = () => {
        setShowAddRuleModal(false);
        setTempModalRule(null);
        setValidationError('');
    };

    const handleOpenEditRuleModal = (rule) => {
        setTempModalRule({ ...rule });
        setCurrentEditingRule({ ...rule });
        setValidationError('');
        setShowEditRuleModal(true);
    };

    const handleCloseEditRuleModal = () => {
        setCurrentEditingRule(null);
        setShowEditRuleModal(false);
        setTempModalRule(null);
        setValidationError('');
    };

    const handleOpenDeleteConfirmModal = (ruleId, priority) => {
        setRuleToDelete({ ruleId, priority });
        setShowDeleteConfirmModal(true);
    };

    const handleCloseDeleteConfirmModal = () => {
        setRuleToDelete(null);
        setShowDeleteConfirmModal(false);
    };

    const handleModalRuleChange = (field, value) => {
        if (field === 'type') {
            setTempModalRule(prev => ({ ...prev, type: value, property: '', fieldId: 0, condition: '', paramValue: '' }));
            setValidationError('');
            return;
        }
        if (field === 'property') {
            setTempModalRule(prev => ({ ...prev, property: value, condition: '', paramValue: '' }));
            setValidationError('');
            return;
        }
        if (field === 'paramValue') {
            setTempModalRule(prev => ({ ...prev, paramValue: value }));
            const error = validateByPropertyAndCondition(tempModalRule?.property, tempModalRule?.condition, value);
            setValidationError(error);
            return;
        }
        setTempModalRule(prev => ({ ...prev, [field]: value }));
    };

    const handleModalFieldChange = (fieldId) => {
        const selectedField = fieldParameter.find(f => f.id === fieldId);
        setTempModalRule(prev => ({
            ...prev,
            fieldId,
            property: selectedField?.type || '',
            condition: '',
            paramValue: '',
        }));
    };

    const handleModalDurationChange = (durationType, value) => {
        setTempModalRule(prev => ({
            ...prev,
            paramValue: { ...(prev.paramValue || {}), [durationType]: value },
        }));
    };

    const persistRuleUpdate = async (updatedRules) => {
        let details = kaiziqResolveConnection.details;
        details.priorityRules = updatedRules;
        await kaiziqResolveConnectionService.updateKaiziqResolveConnectionDetails(
            kaiziqResolveConnection?.id,
            JSON.stringify(details)
        );
        setRules(updatedRules);
    };

    const handleAddRuleSave = async () => {
        if (!tempModalRule?.condition) return;
        const error = validateByPropertyAndCondition(tempModalRule.property, tempModalRule.condition, tempModalRule.paramValue);
        if (error) { setValidationError(error); return; }

        setIsSavingRuleModal(true);
        try {
            const updatedRules = {
                ...rules,
                [tempModalRule.priority]: [...(rules[tempModalRule.priority] || []), tempModalRule],
            };
            await persistRuleUpdate(updatedRules);
            setShowAddRuleModal(false);
            setTempModalRule(null);
            setSnackBarMessage('Rule added successfully');
            setSnackBarType('success');
            setSnackBarOpen(true);
        } catch (err) {
            errorHandler.handleError(err);
            setSnackBarMessage('Error adding rule');
            setSnackBarType('error');
            setSnackBarOpen(true);
        }
        setIsSavingRuleModal(false);
    };

    const handleEditRuleSave = async () => {
        if (!tempModalRule || !currentEditingRule) return;
        const error = validateByPropertyAndCondition(tempModalRule.property, tempModalRule.condition, tempModalRule.paramValue);
        if (error) { setValidationError(error); return; }

        setIsSavingRuleModal(true);
        try {
            const sourcePriority = currentEditingRule.priority;
            const targetPriority = tempModalRule.priority;
            let updatedRules;

            if (sourcePriority === targetPriority) {
                updatedRules = {
                    ...rules,
                    [targetPriority]: rules[targetPriority].map(r => r.id === tempModalRule.id ? tempModalRule : r),
                };
            } else {
                updatedRules = {
                    ...rules,
                    [sourcePriority]: rules[sourcePriority].filter(r => r.id !== tempModalRule.id),
                    [targetPriority]: [...rules[targetPriority], tempModalRule],
                };
            }

            await persistRuleUpdate(updatedRules);
            setShowEditRuleModal(false);
            setCurrentEditingRule(null);
            setTempModalRule(null);
            setSnackBarMessage('Rule updated successfully');
            setSnackBarType('success');
            setSnackBarOpen(true);
        } catch (err) {
            errorHandler.handleError(err);
            setSnackBarMessage('Error updating rule');
            setSnackBarType('error');
            setSnackBarOpen(true);
        }
        setIsSavingRuleModal(false);
    };

    const handleConfirmDelete = async () => {
        if (!ruleToDelete) return;
        const { ruleId, priority } = ruleToDelete;
        setIsSavingRuleModal(true);
        try {
            const updatedRules = {
                ...rules,
                [priority]: rules[priority].filter(r => r.id !== ruleId),
            };
            await persistRuleUpdate(updatedRules);
            setShowDeleteConfirmModal(false);
            setRuleToDelete(null);
            setSnackBarMessage('Rule deleted successfully');
            setSnackBarType('success');
            setSnackBarOpen(true);
        } catch (err) {
            errorHandler.handleError(err);
            setSnackBarMessage('Error deleting rule');
            setSnackBarType('error');
            setSnackBarOpen(true);
        }
        setIsSavingRuleModal(false);
    };

    const handleSnackBarClose = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackBarOpen(false);
    };

    // ── Render helpers ────────────────────────────────────────────────────────

    const currentRules = useMemo(() => rules[selectedPriority] || [], [rules, selectedPriority]);

    const getRuleSummary = (rule) => {
        const typeLabel = rule.type === 'reporter' ? 'Reporter' : 'Field';
        const propertyLabel =
            rule.property === 'fullName' ? 'Full Name' :
            rule.property === 'phoneNumber' ? 'Phone Number' :
            rule.type === 'field'
                ? (formFieldList.find(f => f.id === rule.fieldId)?.title || 'Field')
                : rule.property?.toUpperCase() || '';

        const valueStr = typeof rule.paramValue === 'object'
            ? JSON.stringify(rule.paramValue)
            : `"${rule.paramValue}"`;

        return (
            <div className={`${cn}__ruleItem__summary`}>
                <span className={`${cn}__ruleItem__description`}>
                    {typeLabel}: {propertyLabel}
                </span>
                <div className={`${cn}__ruleItem__meta`}>
                    <span className={`${cn}__ruleItem__badge ${cn}__ruleItem__badge--type`}>{typeLabel}</span>
                    {rule.condition && (
                        <span className={`${cn}__ruleItem__badge ${cn}__ruleItem__badge--condition`}>{rule.condition}</span>
                    )}
                    {rule.paramValue && (
                        <span className={`${cn}__ruleItem__badge ${cn}__ruleItem__badge--value`}>{valueStr}</span>
                    )}
                </div>
            </div>
        );
    };

    const saveDisabled = !tempModalRule?.condition || !!validationError || isSavingRuleModal;

    return (
        <div className={cn} style={{ cursor }}>

            {/* ── Header ── */}
            <div className={`${cn}__header`}>
                <div className={`${cn}__header__left`}>
                    <div className={`${cn}__header__iconWrap`}>
                        <CategoryIcon />
                    </div>
                    <div className={`${cn}__header__text`}>
                        <p className={`${cn}__header__title`}>Priority Category Matrix</p>
                        <p className={`${cn}__header__subtitle`}>Define rules that determine issue priority</p>
                    </div>
                </div>
            </div>

            {/* ── Priority Switcher ── */}
            <div className={`${cn}__prioritySwitcher`}>
                {Object.entries(PRIORITY_CONFIG).map(([key, cfg]) => (
                    <button
                        key={key}
                        className={`${cn}__prioritySwitcher__tab${selectedPriority === key ? ` ${cn}__prioritySwitcher__tab--active` : ''}`}
                        style={{ '--tab-color': cfg.color, '--tab-bg': cfg.bg }}
                        onClick={() => setSelectedPriority(key)}>
                        <span
                            className={`${cn}__prioritySwitcher__tab__dot`}
                            style={{ background: cfg.color }} />
                        {cfg.label}
                    </button>
                ))}
            </div>

            {/* ── Body ── */}
            <div className={`${cn}__body`}>
                {currentRules.length === 0 ? (
                    <div className={`${cn}__empty`}>
                        <CategoryIcon className={`${cn}__empty__icon`} />
                        <p className={`${cn}__empty__text`}>No rules configured</p>
                        <p className={`${cn}__empty__hint`}>
                            Add a rule to define what makes an issue{' '}
                            <strong>{PRIORITY_CONFIG[selectedPriority].label}</strong> priority.
                        </p>
                    </div>
                ) : (
                    <div className={`${cn}__rulesList`}>
                        {currentRules.map((rule, index) => (
                            <div className={`${cn}__ruleItem`} key={rule.id}>
                                <div className={`${cn}__ruleItem__index`}>{index + 1}</div>
                                {getRuleSummary(rule)}
                                <div className={`${cn}__ruleItem__actions`}>
                                    <button
                                        className={`${cn}__ruleItem__btn ${cn}__ruleItem__btn--edit`}
                                        onClick={() => handleOpenEditRuleModal(rule)}
                                        title='Edit Rule'>
                                        <EditIcon />
                                    </button>
                                    <button
                                        className={`${cn}__ruleItem__btn ${cn}__ruleItem__btn--delete`}
                                        onClick={() => handleOpenDeleteConfirmModal(rule.id, rule.priority)}
                                        title='Delete Rule'>
                                        <DeleteIcon />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                <button className={`${cn}__addBtn`} onClick={handleOpenAddRuleModal}>
                    + Add Rule
                </button>
            </div>

            {/* ── Add Rule Modal ── */}
            <AddPriorityRuleModal
                open={showAddRuleModal}
                onClose={handleCloseAddRuleModal}
                onSave={handleAddRuleSave}
                title='Add Rule'
                rule={tempModalRule}
                onRuleChange={handleModalRuleChange}
                onFieldChange={handleModalFieldChange}
                onDurationChange={handleModalDurationChange}
                validationError={validationError}
                ruleTypes={ruleTypes}
                conditions={conditions}
                comparisonOperators={comparisonOperators}
                property={property}
                fieldParameter={fieldParameter}
                formFieldList={formFieldList}
                getAvailableConditions={getAvailableConditions}
                saveDisabled={saveDisabled}
            />

            {/* ── Edit Rule Modal ── */}
            <AddPriorityRuleModal
                open={showEditRuleModal}
                onClose={handleCloseEditRuleModal}
                onSave={handleEditRuleSave}
                title='Edit Rule'
                rule={tempModalRule}
                onRuleChange={handleModalRuleChange}
                onFieldChange={handleModalFieldChange}
                onDurationChange={handleModalDurationChange}
                validationError={validationError}
                ruleTypes={ruleTypes}
                conditions={conditions}
                comparisonOperators={comparisonOperators}
                property={property}
                fieldParameter={fieldParameter}
                formFieldList={formFieldList}
                getAvailableConditions={getAvailableConditions}
                saveDisabled={saveDisabled}
            />

            {/* ── Delete Confirmation ── */}
            <Dialog
                open={showDeleteConfirmModal}
                onClose={handleCloseDeleteConfirmModal}
                className={`${cn}__deleteDialog`}
                maxWidth='xs'
                fullWidth>
                <DialogTitle className={`${cn}__deleteDialog__title`}>
                    Delete Rule
                </DialogTitle>
                <DialogContent className={`${cn}__deleteDialog__content`}>
                    <p className={`${cn}__deleteDialog__text`}>
                        Are you sure you want to delete this rule? This action cannot be undone.
                    </p>
                </DialogContent>
                <DialogActions className={`${cn}__deleteDialog__actions`}>
                    <button
                        className={`${cn}__cancelBtn`}
                        onClick={handleCloseDeleteConfirmModal}
                        disabled={isSavingRuleModal}>
                        Cancel
                    </button>
                    <BorderBlueButtonV2
                        text={isSavingRuleModal ? 'Deleting…' : 'Delete'}
                        onClick={handleConfirmDelete}
                        disabled={isSavingRuleModal}
                    />
                </DialogActions>
            </Dialog>

            <Snackbar
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                open={snackBarOpen}
                autoHideDuration={5000}
                onClose={handleSnackBarClose}>
                <Alert
                    onClose={handleSnackBarClose}
                    severity={snackBarType}
                    variant='filled'
                    sx={{ width: '100%' }}>
                    {snackBarMessage}
                </Alert>
            </Snackbar>
        </div>
    );
}
